export type Route =
  | { name: "shelf" }
  | { name: "viewer"; albumId: string; page: number }
  | { name: "editor"; albumId: string; page: number }
  | { name: "settings" };

type RouteListener = (route: Route) => void;

function parsePath(pathname: string): Route {
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/").filter(Boolean);
  if (parts.length === 0) return { name: "shelf" };
  if (parts[0] === "album" && parts[1] && parts[2] === "view") {
    return { name: "viewer", albumId: parts[1], page: Number(parts[3] ?? "0") || 0 };
  }
  if (parts[0] === "album" && parts[1] && parts[2] === "edit") {
    return { name: "editor", albumId: parts[1], page: Number(parts[3] ?? "0") || 0 };
  }
  if (parts[0] === "settings") return { name: "settings" };
  return { name: "shelf" };
}

function routeToPath(route: Route): string {
  switch (route.name) {
    case "shelf":
      return "/";
    case "viewer":
      return `/album/${route.albumId}/view/${route.page}`;
    case "editor":
      return `/album/${route.albumId}/edit/${route.page}`;
    case "settings":
      return "/settings";
  }
}

class Router {
  private listeners = new Set<RouteListener>();
  current: Route = parsePath(window.location.pathname);

  constructor() {
    window.addEventListener("popstate", () => {
      this.current = parsePath(window.location.pathname);
      this.emit();
    });
  }

  subscribe(listener: RouteListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    for (const listener of this.listeners) listener(this.current);
  }

  /** `silent` updates the URL without remounting the view (in-view paging). */
  navigate(route: Route, replace = false, silent = false): void {
    const path = routeToPath(route);
    if (replace) history.replaceState(null, "", path);
    else history.pushState(null, "", path);
    this.current = route;
    if (!silent) this.emit();
  }
}

export const router = new Router();
